<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <li
        class="el-submenu"
        :class="{ 'is-opened': opened, 'is-active': active }"
        role="menuitem"
        :title="computedTitle"
    >
        <div
            ref="submenuTitle"
            class="el-submenu__item"
            :class="{
                'is-root': level === 1,
                'is-multiline': multiline,
                'is-focus': isFocus
            }"
            tabindex="0"
            @click="handleClick"
            @mousedown="isFocus = true"
            @mouseup="isFocus = false"
            @focus="isFocus = true"
            @blur="isFocus = false"
            @keyup.enter="handleClick"
            @keydown.enter.prevent="handleClick"
        >
            <a-glyph v-if="showArrow" class="el-submenu__arrow" :name="glyph" />
            <span v-if="$slots.icon" class="el-menu__bar-icon">
                <slot name="icon" />
            </span>
            <span class="el-text el-text--body-24 el-text--ellipsis">
                <slot name="title" />
            </span>
        </div>

        <ul v-show="opened" class="el-menu__bar" role="menu">
            <div :class="[`el-menu__bar--level-${level}`]">
                <slot />
            </div>
        </ul>
    </li>
</template>

<script setup>
import { ref, computed, inject, provide, useSlots, onMounted, onBeforeUnmount } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';

const slots = useSlots();
const props = defineProps({
    index: { type: String, required: true },
    multiline: { type: Boolean, default: false },
    showHoverHint: { type: Boolean, default: false },
    glyph: { type: String, default: 'i-chevron-right--16' },
});

const submenuTitle = ref(null);
const isFocus = ref(false);

const menuContext = inject('menuContext');
const parentMenu = inject('parentMenu', null);
const indexPath = computed(() => [...(parentMenu?.indexPath || []), props.index]);
const level = computed(() => (parentMenu?.level || 0) + 1);
const opened = computed(() => menuContext.openedMenus?.value.includes(props.index));

const active = computed(() => {
    const current = menuContext.activeIndex?.value;
    return indexPath.value.includes(current);
});

const showArrow = computed(() => menuContext.type === 'secondary');

const computedTitle = computed(() => {
    if (!props.showHoverHint) return null;
    const nodes = [...(slots.title?.() || slots.default?.() || [])];
    return nodes.map(node => node.children?.toString?.() || '').join('').trim();
});

function handleClick() {
    menuContext.handleSubmenuClick({
        index: props.index,
        indexPath: indexPath.value,
        $el: submenuTitle.value,
        slots,
    });
}

onMounted(() => {
    menuContext.addSubmenu({ index: props.index, indexPath: indexPath.value, $el: submenuTitle.value });
});

onBeforeUnmount(() => {
    menuContext.closeMenu?.(props.index);
    menuContext.removeSubmenu({ index: props.index });
});

provide('parentMenu', { name: 'Submenu', indexPath: indexPath.value, level: level.value });
</script>
